import React, { useEffect, useRef, useState } from 'react';
import styled, { keyframes, css } from 'styled-components';

// ─── Animation Keyframes ────────────────────────────────────────────

const revealUp = keyframes`
  from { opacity: 0; transform: translateY(80px); }
  to { opacity: 1; transform: translateY(0); }
`;

const revealDown = keyframes`
  from { opacity: 0; transform: translateY(-80px); }
  to { opacity: 1; transform: translateY(0); }
`;

const revealLeft = keyframes`
  from { opacity: 0; transform: translateX(-80px); }
  to { opacity: 1; transform: translateX(0); }
`;

const revealRight = keyframes`
  from { opacity: 0; transform: translateX(80px); }
  to { opacity: 1; transform: translateX(0); }
`;

const revealScale = keyframes`
  from { opacity: 0; transform: scale(0.7); }
  to { opacity: 1; transform: scale(1); }
`;

const revealRotate = keyframes`
  from { opacity: 0; transform: rotate(-8deg) translateY(50px); }
  to { opacity: 1; transform: rotate(0deg) translateY(0); }
`;

const revealFlipX = keyframes`
  from { opacity: 0; transform: perspective(800px) rotateX(90deg); }
  to { opacity: 1; transform: perspective(800px) rotateX(0deg); }
`;

const revealFlipY = keyframes`
  from { opacity: 0; transform: perspective(800px) rotateY(90deg); }
  to { opacity: 1; transform: perspective(800px) rotateY(0deg); }
`;

const revealBlur = keyframes`
  from { opacity: 0; filter: blur(20px); transform: scale(1.05); }
  to { opacity: 1; filter: blur(0); transform: scale(1); }
`;

const revealBounce = keyframes`
  0% { opacity: 0; transform: translateY(80px) scale(0.9); }
  60% { opacity: 1; transform: translateY(-15px) scale(1.02); }
  80% { transform: translateY(5px) scale(0.99); }
  100% { opacity: 1; transform: translateY(0) scale(1); }
`;

const revealZoom = keyframes`
  from { opacity: 0; transform: scale(0.3); }
  70% { opacity: 1; transform: scale(1.05); }
  100% { opacity: 1; transform: scale(1); }
`;

const revealSlideRotate = keyframes`
  from { opacity: 0; transform: translateX(-60px) rotate(-15deg); }
  to { opacity: 1; transform: translateX(0) rotate(0deg); }
`;

const revealSpring = keyframes`
  0% { opacity: 0; transform: translateY(100px) scale(0.8); }
  50% { opacity: 1; transform: translateY(-20px) scale(1.05); }
  70% { transform: translateY(10px) scale(0.98); }
  85% { transform: translateY(-5px) scale(1.01); }
  100% { opacity: 1; transform: translateY(0) scale(1); }
`;

const revealFade = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const animations = {
  up: revealUp,
  down: revealDown,
  left: revealLeft,
  right: revealRight,
  scale: revealScale,
  rotate: revealRotate,
  flipX: revealFlipX,
  flipY: revealFlipY,
  blur: revealBlur,
  bounce: revealBounce,
  zoom: revealZoom,
  slideRotate: revealSlideRotate,
  spring: revealSpring,
  fade: revealFade,
};

const RevealWrapper = styled.div`
  opacity: 0;
  will-change: opacity, transform;
  
  ${props => props.$isVisible && css`
    animation: ${animations[props.$direction] || revealUp} 
      ${props.$duration || '0.8s'} 
      ${props.$easing || 'cubic-bezier(0.25, 0.46, 0.45, 0.94)'} 
      ${props.$delay || '0s'} 
      forwards;
  `}
`;

/**
 * ScrollReveal component - triggers animations when element scrolls into view
 * 
 * Directions: 'up' | 'down' | 'left' | 'right' | 'scale' | 'rotate' | 
 *             'flipX' | 'flipY' | 'blur' | 'bounce' | 'zoom' | 
 *             'slideRotate' | 'spring' | 'fade'
 */
const ScrollReveal = ({ 
  children, 
  direction = 'up', 
  delay = '0s', 
  duration = '0.8s',
  easing = 'cubic-bezier(0.25, 0.46, 0.45, 0.94)',
  threshold = 0.15,
  once = true,
  style,
  className
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          if (once) observer.unobserve(element);
        } else if (!once) {
          setIsVisible(false);
        }
      },
      { threshold, rootMargin: '0px 0px -50px 0px' }
    );

    observer.observe(element);
    return () => observer.unobserve(element);
  }, [threshold, once]);

  return (
    <RevealWrapper
      ref={ref}
      $isVisible={isVisible}
      $direction={direction}
      $delay={delay}
      $duration={duration}
      $easing={easing}
      style={style}
      className={className}
    >
      {children}
    </RevealWrapper>
  );
};

/**
 * useInView hook
 */
export const useInView = (options = {}) => {
  const { threshold = 0.15, once = true, rootMargin = '0px 0px -50px 0px' } = options;
  const [isInView, setIsInView] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          if (once) observer.unobserve(element);
        } else if (!once) {
          setIsInView(false);
        }
      },
      { threshold, rootMargin }
    );

    observer.observe(element);
    return () => observer.unobserve(element);
  }, [threshold, once, rootMargin]);

  return [ref, isInView];
};

/**
 * useCountUp hook
 */
export const useCountUp = (end, duration = 2000, startOnView = true) => {
  const [count, setCount] = useState(0);
  const [ref, isInView] = useInView({ threshold: 0.3 });
  const hasAnimated = useRef(false);

  useEffect(() => {
    if (!startOnView || !isInView || hasAnimated.current) return;
    hasAnimated.current = true;

    const numericEnd = parseInt(end);
    if (isNaN(numericEnd)) { setCount(end); return; }

    let startTime = null;
    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * numericEnd));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [isInView, end, duration, startOnView]);

  return [ref, count];
};

/**
 * useMouseParallax hook - creates a parallax effect following the mouse
 */
export const useMouseParallax = (intensity = 20) => {
  const ref = useRef(null);
  const [offset, setOffset] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!ref.current) return;
      const rect = ref.current.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      const x = ((e.clientX - centerX) / rect.width) * intensity;
      const y = ((e.clientY - centerY) / rect.height) * intensity;
      setOffset({ x, y });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [intensity]);

  return [ref, offset];
};

// ─── Wave Divider Component ─────────────────────────────────────────

const waveFloat = keyframes`
  0%, 100% { transform: translateX(0); }
  50% { transform: translateX(-25px); }
`;

const WaveSvg = styled.svg`
  display: block;
  width: 100%;
  height: ${props => props.$height || '80px'};
  position: relative;
  z-index: 2;
  margin-top: -1px;
  margin-bottom: -1px;
  
  path {
    animation: ${waveFloat} ${props => props.$speed || '8s'} ease-in-out infinite;
  }
  
  @media (max-width: 768px) {
    height: ${props => `${parseInt(props.$height || 80) * 0.6}px`};
  }
`;

export const WaveDivider = ({ 
  color = '#f8fafc', 
  flip = false, 
  variant = 1, 
  height = '80px',
  speed = '8s'
}) => {
  const paths = {
    1: 'M0,32 C200,80 400,0 600,48 C800,96 1000,16 1200,40 L1200,100 L0,100 Z',
    2: 'M0,64 C150,20 350,90 500,50 C650,10 850,80 1000,45 C1100,25 1200,55 1200,40 L1200,100 L0,100 Z',
    3: 'M0,40 C100,80 200,10 350,60 C500,110 600,20 750,50 C900,80 1050,30 1200,55 L1200,100 L0,100 Z',
    4: 'M0,50 C300,10 350,90 600,40 C850,-10 900,70 1200,45 L1200,100 L0,100 Z',
  };

  return (
    <WaveSvg 
      viewBox="0 0 1200 100" 
      preserveAspectRatio="none" 
      $height={height}
      $speed={speed}
      style={{ transform: flip ? 'rotate(180deg)' : 'none' }}
    >
      <path d={paths[variant] || paths[1]} fill={color} />
    </WaveSvg>
  );
};

// ─── Floating Shape Component ───────────────────────────────────────

const shapeFloat = keyframes`
  0%, 100% { transform: translateY(0) rotate(0deg); }
  25% { transform: translateY(-20px) rotate(5deg); }
  50% { transform: translateY(-10px) rotate(-3deg); }
  75% { transform: translateY(-25px) rotate(4deg); }
`;

const ShapeElement = styled.div`
  position: absolute;
  width: ${props => props.$size || '60px'};
  height: ${props => props.$size || '60px'};
  background: ${props => props.$gradient || 'linear-gradient(135deg, rgba(59,130,246,0.08), rgba(16,185,129,0.08))'};
  border-radius: ${props => props.$shape === 'circle' ? '50%' : props.$shape === 'rounded' ? '30%' : '0'};
  transform: rotate(${props => props.$rotation || '0deg'});
  animation: ${shapeFloat} ${props => props.$duration || '12s'} ease-in-out infinite;
  animation-delay: ${props => props.$delay || '0s'};
  top: ${props => props.$top};
  left: ${props => props.$left};
  right: ${props => props.$right};
  bottom: ${props => props.$bottom};
  z-index: 0;
  pointer-events: none;
  opacity: 0.6;
  
  @media (max-width: 768px) {
    opacity: 0.3;
    width: ${props => `${parseInt(props.$size || 60) * 0.6}px`};
    height: ${props => `${parseInt(props.$size || 60) * 0.6}px`};
  }
`;

export const FloatingShape = (props) => <ShapeElement {...props} />;

// ─── Stagger Children Component ─────────────────────────────────────

const StaggerContainer = styled.div`
  & > * {
    opacity: 0;
    will-change: opacity, transform;
  }
  
  ${props => props.$isVisible && css`
    & > * {
      animation: ${animations[props.$direction] || revealUp} 
        ${props.$duration || '0.6s'} 
        cubic-bezier(0.25, 0.46, 0.45, 0.94) 
        forwards;
    }
    
    ${Array.from({ length: 20 }, (_, i) => css`
      & > *:nth-child(${i + 1}) {
        animation-delay: ${(props.$baseDelay || 0) + i * (props.$stagger || 0.1)}s;
      }
    `)}
  `}
`;

export const StaggerReveal = ({
  children,
  direction = 'up',
  stagger = 0.1,
  baseDelay = 0,
  duration = '0.6s',
  threshold = 0.1,
  style,
  className
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(element);
        }
      },
      { threshold, rootMargin: '0px 0px -30px 0px' }
    );

    observer.observe(element);
    return () => observer.unobserve(element);
  }, [threshold]);

  return (
    <StaggerContainer
      ref={ref}
      $isVisible={isVisible}
      $direction={direction}
      $stagger={stagger}
      $baseDelay={baseDelay}
      $duration={duration}
      style={style}
      className={className}
    >
      {children}
    </StaggerContainer>
  );
};

export default ScrollReveal;
